<template>
    <div class="search-page">
        <top-filter
            :initial-params="params"
            ref="refTopFilter"
            @change="onTopFilterChange"
            @clear="onUpdateParams"
            class="u-bottom-margin--l"
        />
        <div class="row">
            <div class="col-default-8  col-v-tablet-12">
                <sort-and-sections
                    class="search-page__sort-and-sections u-bottom-margin--s"
                    :class="{ 'is-disabled': isDisabledFilters }"
                    :sections="data?.filters?.sections"
                    :initial-params="params"
                    ref="refSorting"
                    @change="onFilterChange"
                    @clear="onUpdateParams"
                />
            </div>
            <div class="col-default-3  col-v-tablet-5 col-mobile-12 col-default-offset-1 col-v-tablet-offset-0">
                <aside-filter
                    v-show="!isInitState"
                    :data="data.filters"
                    :initial-params="params"
                    ref="refAsideFilter"
                    @change="onFilterChange"
                    @clear="onUpdateParams"
                    class="search-page__aside-filters"
                    :class="{ 'is-disabled': isDisabledFilters }"
                />
            </div>
        </div>

        <div class="row" v-show="!isInitState">
            <div class="col-default-8 col-tablet-12">

                <div class="search-page__preloader">
                    <z-preloader v-if="isLoading" />
                </div>
                <results class="u-top-margin--s" :data="items" v-if="items.length" />

                <z-not-found class="search-page__not-found" v-show="!isLoading && !items.length">
                    <template v-slot:description>
                        <div class="u-bottom-margin--2xs" v-html="text.description"></div>
                        <z-link
                            tag="button"
                            @click="resetFilters"
                            icon-position="right"
                            icon="tools/close-small"
                            :underlined="false"
                            v-if="isFilterActive"
                        >
                            <span v-html="text.resetFilters"></span>
                        </z-link>
                    </template>
                </z-not-found>

                <z-pagination
                    :data="paginationData"
                    @change-page="onPageChange"
                    class="u-top-margin--xl-important"
                    :loading="isLoading"
                >
                </z-pagination>
            </div>
        </div>

        <div class="search-page__init-img" v-show="isInitState">
            <img src="/images/search.png" alt="">
        </div>

    </div>
</template>

<script setup>
import TopFilter from './components/TopFilter.vue'
import SortAndSections from './components/SortAndSections.vue'

import AsideFilter from './components/AsideFilter.vue'


import Results from './components/Results.vue'
import { ref, computed, watch, onBeforeMount } from 'vue'
import { useGetData } from '@/composable/useGetData'
import { useGetter } from '@/composable/useGetter'
import { useHandlers } from './composable/useHandlers'
import { getSearchData } from '@/api/search'
import { text } from './locales'

const params = ref({
    page: 1,
    from: '',
    to: '',
    tags: [],
    q: '',
    how: 'r',
    section: '',
})

const isInitState = ref(true)
const { app } = useGetter(['app'])
const isMore = ref(false)
const appData = app.value.components?.search
const { isLoading, data, fetcher } = useGetData(getSearchData, params, appData)
const {
    onPageChange,
    onUpdateParams,
    onFilterChange,
    setGetParams,
    setInitParams
} = useHandlers(params, isMore, fetcher)

const items = ref(data.value.items || [])
const refSorting = ref(null)
const refAsideFilter = ref(null)
const refTopFilter = ref(null)

const isFilterActive = computed(() => {
    let active = false

    for (const key in params.value) {
        if (['page', 'how', 'q'].includes(key)) continue
        if (Array.isArray(params.value[key])) {
            if (params.value[key].length) {
                active = true
                break
            }
        } else {
            if (params.value[key]) {
                active = true
                break
            }
        }
    }

    return active
})

const isDisabledFilters = computed(() => {
    return (params.value.q?.length && items.value.length === 0 && !isFilterActive.value)
})

const paginationData = computed(() => {
    return ({
        currentPage: data.value.nav?.current || 0,
        totalItems: data.value.nav?.count || 0,
        totalPages: data.value.nav?.total || 0,
        pageRange: 1
    })
})

const resetFilters = () => {
    // refTopFilter.value?.clear()
    refSorting.value?.clear()
    refAsideFilter.value?.clear()

    onFilterChange({})
    setGetParams()
}

const onTopFilterChange = data => {
    isInitState.value = false
    onFilterChange(data)
}

watch(data, () => {
    if (!(data.value.items && data.value.items.length)) {
        items.value = []
        return
    }

    items.value = isMore.value ? [...items.value, ...data.value.items] : items.value = data.value.items
})

onBeforeMount(() => {
    setInitParams()
    if (params.value.q.length) isInitState.value = false
})
</script>

<style lang="scss">
.search-page {

    &__init-img {
        width: min(240px, 90%);
        display: flex;
        margin: 0 auto;
        @include margin-level(top, XL);

        img {
            width: 100%;
        }
    }

    &__not-found {
        margin: 0 auto;
        @include margin-level(top, L);
        width: 100%;
    }

    &__preloader {
        position: relative;
        height: 24px;
        pointer-events: none;

        .z-preloader {
            position: absolute;
            width: max-content;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            margin: 0;
            padding: 0;
        }
    }

    &__sort-and-sections,
    &__aside-filters {
        &.is-disabled {
            opacity: 0.4;
            pointer-events: none;
        }
    }
}
</style>
